import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, ArrowRight, MessageCircle } from 'lucide-react';
import { cpcsCategories, heroImages, companyInfo } from '../data/mock';

const CPCSCoursesPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-construction-darkblue text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={heroImages.alt2} 
            alt="CPCS Training" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              CPCS Plant Operator Training
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Become a certified plant machinery operator with our comprehensive CPCS training courses
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              Book Your Course
            </a>
          </div>
        </div>
      </section>

      {/* What is CPCS */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-6">
              What is CPCS?
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                CPCS (Construction Plant Competence Scheme) is the leading plant operator card scheme in the UK construction industry. It provides proof that operators have been trained and tested to industry-recognized standards.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>Most UK construction sites require plant operators to hold a valid CPCS card</strong> to operate machinery such as excavators, dumpers, telehandlers, and rollers.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-red-700 mb-3 flex items-center gap-2">
                    <div className="bg-red-500 text-white px-3 py-1 rounded text-sm">Red Card</div>
                    Trained Operator
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                      <span>Valid for 2 years</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                      <span>Issued after passing test</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                      <span>Provisional operator status</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                      <span>Must complete NVQ within 2 years</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <h3 className="text-xl font-bold text-blue-700 mb-3 flex items-center gap-2">
                    <div className="bg-blue-600 text-white px-3 py-1 rounded text-sm">Blue Card</div>
                    Competent Operator
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                      <span>Valid for 5 years</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                      <span>Requires NVQ completion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                      <span>Fully competent status</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                      <span>Higher earning potential</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer Banner */}
      <section className="py-8 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">
                  🎉 Special Offer - Book & Save!
                </h3>
                <p className="text-lg text-green-100 mb-2">
                  <strong>FREE CSCS Test + FREE CPCS Card</strong> when you book any training course with us!
                </p>
                <p className="text-green-200">
                  For additional discounts, call us directly:
                </p>
              </div>
              <div className="flex flex-col items-center">
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-xl hover:bg-yellow-400 transition-all hover:scale-105 shadow-lg"
                >
                  <Phone size={24} />
                  {companyInfo.phone}
                </a>
                <span className="text-green-200 text-sm mt-2">Call now for exclusive discounts!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CPCS Categories */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
              Popular CPCS Categories
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional training for all major plant machinery types
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {cpcsCategories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-200"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="inline-block bg-construction-yellow/20 text-construction-darkblue px-3 py-1 rounded-full text-xs font-bold mb-2">
                        {category.code}
                      </span>
                      <h3 className="text-2xl font-bold text-construction-darkblue">
                        {category.name}
                      </h3>
                      <p className="text-construction-yellow font-semibold">
                        {category.type}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {category.description}
                  </p>

                  <div className="space-y-2 mb-6 pb-6 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="text-construction-yellow" size={18} />
                      <span className="text-gray-700">
                        <strong>Duration:</strong> {category.duration}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-construction-darkblue">
                      {category.price}
                    </div>
                  </div>

                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="block text-center bg-construction-yellow text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all hover:scale-105"
                  >
                    Book This Course
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Looking for a different CPCS category?
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-construction-yellow font-bold hover:text-yellow-600 transition-colors"
            >
              View All Available Categories
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Course Structure */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-12 text-center">
              CPCS Course Structure
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-construction-darkblue mb-4">
                  Training Phase
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Health & safety fundamentals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Machine pre-use checks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Operating procedures</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Practical skills development</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Site safety protocols</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-construction-darkblue mb-4">
                  Testing Phase
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Written theory examination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Practical assessment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Independent examiner evaluation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Immediate results</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-construction-yellow flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-700">Red CPCS card issued on pass</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-construction-darkblue mb-3">
                Upgrade to Blue Card
              </h3>
              <p className="text-gray-700 mb-3">
                Within your 2-year Red card period, you must complete an NVQ Level 2 in Plant Operations to upgrade to a Blue CPCS card (valid for 5 years).
              </p>
              <p className="text-gray-700">
                <strong>We can help you with the NVQ process!</strong> Contact us for more information about upgrading your Red card to Blue.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-construction-darkblue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Your CPCS Training Today
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Become a qualified plant operator and increase your earning potential
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-construction-yellow text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all hover:scale-105"
            >
              <Phone size={24} />
              {companyInfo.phone}
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I'm interested in CPCS training`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105"
            >
              <MessageCircle size={24} />
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105"
            >
              Request Information
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CPCSCoursesPage;
